import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  ArrowLeft,
  Loader2,
  Copy,
  Download,
  Clock,
  Calendar,
  Terminal,
} from 'lucide-react';
import { fetchAdminTask } from '@/api/admin';
import { toast } from 'sonner';

export function AdminTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-task', taskId],
    queryFn: () => fetchAdminTask(Number(taskId)),
    enabled: !!taskId,
  });

  const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${seconds} seconds`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m ${secs}s`;
  };

  const getStatusBadge = (status: string) => {
    const lower = status.toLowerCase();
    if (lower === 'completed' || lower === 'success') {
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{status}</Badge>;
    }
    if (lower === 'failed' || lower === 'error') {
      return <Badge variant="destructive">{status}</Badge>;
    }
    if (lower === 'running' || lower === 'pending' || lower === 'queued') {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{status}</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const downloadLog = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Log downloaded');
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load task.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  const combinedLog = [data.output_log, data.error_log].filter(Boolean).join('\n\n--- ERROR LOG ---\n\n');

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/tasks">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tasks
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">Task #{data.id}</h1>
            <p className="text-sm text-muted-foreground">{data.title || 'Untitled task'}</p>
          </div>
        </div>
        {getStatusBadge(data.status)}
      </div>

      {/* Task Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Terminal className="h-4 w-4" />
              Task Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <Badge variant="outline">{data.task_type || '—'}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium">{data.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project</span>
              <span className="font-medium">
                {data.project_name || (data.project_id ? `#${data.project_id}` : '—')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Build</span>
              <span className="font-medium text-xs">
                {data.build_fqdn || (data.build_id ? `#${data.build_id}` : '—')}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Timestamps
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created</span>
              <span className="font-medium text-xs">
                {data.created ? new Date(data.created).toLocaleString() : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Started</span>
              <span className="font-medium text-xs">
                {data.started_at ? new Date(data.started_at).toLocaleString() : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Completed</span>
              <span className="font-medium text-xs">
                {data.completed_at ? new Date(data.completed_at).toLocaleString() : '—'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{formatDuration(data.duration)}</p>
            {data.duration !== null && data.duration !== undefined && (
              <p className="text-sm text-muted-foreground mt-1">
                {data.duration} seconds total
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Command */}
      {data.command && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Command</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(data.command!, 'Command')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
              {data.command}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Output Log */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Output Log</CardTitle>
            <div className="flex gap-2">
              {data.output_log && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(data.output_log!, 'Output log')}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadLog(data.output_log!, `task-${data.id}-output.log`)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
            {data.output_log || 'No output'}
          </pre>
        </CardContent>
      </Card>

      {/* Error Log */}
      {data.error_log && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-red-600">Error Log</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(data.error_log!, 'Error log')}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadLog(data.error_log!, `task-${data.id}-error.log`)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-red-950 text-red-100 rounded-lg p-4 text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
              {data.error_log}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Download All Logs Button */}
      {(data.output_log || data.error_log) && (
        <div className="mt-6 flex justify-end">
          <Button
            variant="outline"
            onClick={() => downloadLog(combinedLog, `task-${data.id}-full.log`)}
          >
            <Download className="h-4 w-4 mr-2" />
            Download All Logs
          </Button>
        </div>
      )}
    </AdminLayout>
  );
}
